/**
 * TOTP Setup Component
 * Allows users to set up Two-Factor Authentication for SCADA write operations
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import {
  Security as SecurityIcon,
  PhoneAndroid as PhoneIcon,
  QrCode as QrCodeIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { scadaWriteAPI } from '../../services/scadaWriteService';
import { TOTPDevice, TOTPSetupResponse } from '../../types/scada';

interface TOTPSetupProps {
  onSetupComplete?: () => void;
}

const steps = [
  'Install Authenticator App',
  'Scan QR Code',
  'Verify Setup',
  'Save Backup Codes'
];

export const TOTPSetup: React.FC<TOTPSetupProps> = ({ onSetupComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totpDevice, setTotpDevice] = useState<TOTPDevice | null>(null);
  const [setupData, setSetupData] = useState<TOTPSetupResponse | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [backupCodesDialogOpen, setBackupCodesDialogOpen] = useState(false);
  const [deviceStatus, setDeviceStatus] = useState<TOTPDevice | null>(null);

  useEffect(() => {
    checkDeviceStatus();
  }, []);

  const checkDeviceStatus = async () => {
    try {
      setLoading(true);
      const status = await scadaWriteAPI.getTOTPDevice();
      setDeviceStatus(status);
      
      if (status && status.is_verified) {
        setActiveStep(4); // Device already set up
      }
    } catch (err: any) {
      setError('Failed to check TOTP device status');
    } finally {
      setLoading(false);
    }
  };

  const startSetup = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await scadaWriteAPI.setupTOTP({
        device_name: 'ReGenOps SCADA'
      });
      
      setSetupData(response);
      setBackupCodes(response.backup_tokens || []);
      setActiveStep(1);
    } catch (err: any) {
      setError(err.message || 'Failed to start TOTP setup');
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await scadaWriteAPI.verifyTOTP({
        totp_code: verificationCode
      });
      
      // Update backup codes from verification response
      if (response.backup_codes && response.backup_codes.length > 0) {
        setBackupCodes(response.backup_codes);
        setBackupCodesDialogOpen(true);
      }
      
      setActiveStep(3);
      onSetupComplete?.();
      
    } catch (err: any) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    copyToClipboard(codesText);
  };

  if (loading && !deviceStatus) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Two-Factor Authentication (2FA)
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Secure your SCADA write operations with two-factor authentication. 
        You'll need an authenticator app like Google Authenticator or Authy.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Current Status */}
      {deviceStatus && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Current Status</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              {deviceStatus && deviceStatus.is_verified ? (
                <>
                  <Chip
                    icon={<CheckIcon />}
                    label="2FA Enabled"
                    color="success"
                    variant="outlined"
                  />
                  <Typography variant="body2">
                    Device: {deviceStatus.device_name}
                  </Typography>
                  {deviceStatus.last_used && (
                    <Typography variant="body2" color="text.secondary">
                      Last used: {new Date(deviceStatus.last_used).toLocaleString()}
                    </Typography>
                  )}
                </>
              ) : deviceStatus ? (
                <>
                  <Chip
                    icon={<WarningIcon />}
                    label="Setup Incomplete"
                    color="warning"
                    variant="outlined"
                  />
                  <Typography variant="body2">
                    Please complete the verification process
                  </Typography>
                </>
              ) : (
                <>
                  <Chip
                    icon={<WarningIcon />}
                    label="2FA Not Configured"
                    color="error"
                    variant="outlined"
                  />
                  <Typography variant="body2">
                    Two-factor authentication is required for SCADA write operations
                  </Typography>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Setup Stepper */}
      {(!deviceStatus || !deviceStatus.is_verified) && (
        <Card>
          <CardContent>
            <Stepper activeStep={activeStep} orientation="vertical">
              {/* Step 0: Install App */}
              <Step>
                <StepLabel>Install Authenticator App</StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Install an authenticator app on your mobile device:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <PhoneIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Google Authenticator"
                        secondary="Available on iOS and Android"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PhoneIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Microsoft Authenticator"
                        secondary="Available on iOS and Android"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PhoneIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Authy"
                        secondary="Multi-device support"
                      />
                    </ListItem>
                  </List>
                  <Button
                    variant="contained"
                    onClick={startSetup}
                    disabled={loading}
                    sx={{ mt: 2 }}
                  >
                    {loading ? <CircularProgress size={20} /> : 'Continue'}
                  </Button>
                </StepContent>
              </Step>

              {/* Step 1: Scan QR Code */}
              <Step>
                <StepLabel>Scan QR Code</StepLabel>
                <StepContent>
                  {setupData && (
                    <>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Scan this QR code with your authenticator app:
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          {setupData.qr_code_url ? (
                            <Box
                              component="img"
                              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.qr_code_url)}`}
                              alt="TOTP QR Code"
                              sx={{
                                width: 200,
                                height: 200,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1
                              }}
                              onError={(e) => {
                                console.error('QR code image failed to load:', e);
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : null}
                          
                          {!setupData.qr_code_url && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                              QR code generation not available. Please use manual entry below.
                            </Alert>
                          )}
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Manual Entry (if QR code doesn't work):
                          </Typography>
                          <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 2 }}>
                            <Typography variant="body2" component="div">
                              <strong>Account:</strong> ReGenOps SCADA<br/>
                              <strong>Key:</strong> {setupData.manual_entry_key || setupData.secret_key}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            startIcon={<CopyIcon />}
                            onClick={() => copyToClipboard(setupData.manual_entry_key || setupData.secret_key)}
                          >
                            Copy Secret Key
                          </Button>
                        </Grid>
                      </Grid>
                      
                      <Button
                        variant="contained"
                        onClick={() => setActiveStep(2)}
                        sx={{ mt: 2 }}
                      >
                        Continue
                      </Button>
                    </>
                  )}
                </StepContent>
              </Step>

              {/* Step 2: Verify */}
              <Step>
                <StepLabel>Verify Setup</StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Enter the 6-digit code from your authenticator app:
                  </Typography>
                  
                  <TextField
                    label="Verification Code"
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setVerificationCode(value);
                    }}
                    placeholder="123456"
                    inputProps={{ maxLength: 6 }}
                    sx={{ mb: 2 }}
                  />
                  
                  <Box>
                    <Button
                      variant="contained"
                      onClick={verifySetup}
                      disabled={loading || verificationCode.length !== 6}
                      sx={{ mr: 1 }}
                    >
                      {loading ? <CircularProgress size={20} /> : 'Verify'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setActiveStep(1)}
                    >
                      Back
                    </Button>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 3: Backup Codes */}
              <Step>
                <StepLabel>Save Backup Codes</StepLabel>
                <StepContent>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      2FA setup complete! Make sure to save your backup codes.
                    </Typography>
                  </Alert>
                  
                  <Button
                    variant="outlined"
                    onClick={() => setBackupCodesDialogOpen(true)}
                  >
                    View Backup Codes
                  </Button>
                </StepContent>
              </Step>
            </Stepper>
          </CardContent>
        </Card>
      )}

      {/* Backup Codes Dialog */}
      <Dialog
        open={backupCodesDialogOpen}
        onClose={() => setBackupCodesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Backup Recovery Codes
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Save these backup codes in a secure location. You can use them to access your account if you lose your authenticator device.
          </Alert>
          
          <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 2 }}>
            {backupCodes.map((code, index) => (
              <Typography key={index} variant="body2" component="div" sx={{ fontFamily: 'monospace' }}>
                {code}
              </Typography>
            ))}
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={copyBackupCodes}
            fullWidth
          >
            Copy All Codes
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupCodesDialogOpen(false)}>
            I've Saved My Codes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
